<template>
  <AppNavbar />
  <div class="particles" id="particles"></div>
  <router-view />
  <AppFooter />
</template>

<script>
import AppNavbar from '@/components/AppNavbar.vue'
import AppFooter from '@/components/AppFooter.vue'
export default {
  name: "App",
  components: {
    AppNavbar,
    AppFooter
  },
  mounted() {
    // Particle System
    function createParticles() {
      const particles = document.getElementById('particles');
      const particleCount = 50;

      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div');
        particle.className = 'particle';
        particle.style.left = Math.random() * 100 + '%';
        particle.style.top = Math.random() * 100 + '%';
        particle.style.animationDelay = Math.random() * 6 + 's';
        particle.style.animationDuration = (Math.random() * 3 + 3) + 's';
        particles.appendChild(particle);
      }
    }

    // Navigation Dots Functionality
    function initNavigation() {
      const navDots = document.querySelectorAll('.nav-dot');
      const sections = document.querySelectorAll('.flow-section');

      navDots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          sections[index].scrollIntoView({ behavior: 'smooth' });
        });
      });

      // Update active dot on scroll
      window.addEventListener('scroll', () => {
        let current = '';
        sections.forEach((section, index) => {
          const rect = section.getBoundingClientRect();
          if (rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2) {
            current = index;
          }
        });

        navDots.forEach((dot, index) => {
          dot.classList.toggle('active', index === current);
        });
      });
    }

    // Intersection Observer for animations
    function initScrollAnimations() {
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }
        });
      }, observerOptions);

      // Observe elements for animation
      const animatedElements = document.querySelectorAll('.card, .achievement-item, .detail-card, .contact-item');
      animatedElements.forEach(el => {
        el.style.opacity = '0';
        el.style.transform = 'translateY(50px)';
        el.style.transition = 'all 0.6s ease';
        observer.observe(el);
      });
    }

    // Smooth scrolling for anchor links
    function initSmoothScrolling() {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function (e) {
          e.preventDefault();
          const target = document.querySelector(this.getAttribute('href'));
          if (target) {
            target.scrollIntoView({ behavior: 'smooth' });
          }
        });
      });
    }

    // Add dynamic background to hero
    function initDynamicBackground() {
      const hero = document.querySelector('.hero');
      if (!hero) return;
      let mouseX = 0, mouseY = 0;

      hero.addEventListener('mousemove', (e) => {
        mouseX = e.clientX / window.innerWidth;
        mouseY = e.clientY / window.innerHeight;

        hero.style.background = `radial-gradient(circle at ${mouseX * 100}% ${mouseY * 100}%, rgba(139, 92, 246, 0.2) 0%, var(--primary-dark) 70%)`;
      });
    }

    // Enhanced card interactions
    function initCardInteractions() {
      const cards = document.querySelectorAll('.card, .detail-card, .contact-item');

      cards.forEach(card => {
        card.addEventListener('mouseenter', function () {
          this.style.transform = 'translateY(-15px) scale(1.02)';
          this.style.boxShadow = '0 20px 40px rgba(255, 107, 107, 0.1)';
        });

        card.addEventListener('mouseleave', function () {
          this.style.transform = 'translateY(0) scale(1)';
          this.style.boxShadow = 'none';
        });
      });
    }

    // Initialize everything when DOM is loaded
    document.addEventListener('DOMContentLoaded', function () {
      createParticles();
      initNavigation();
      initScrollAnimations();
      initSmoothScrolling();
      initDynamicBackground();
      initCardInteractions();

      // Add loading animation
      document.body.style.opacity = '0';
      setTimeout(() => {
        document.body.style.transition = 'opacity 1s ease';
        document.body.style.opacity = '1';
      }, 100);
    });

    // Performance optimization: throttle scroll events
    function throttle(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    // Add parallax effect to sections
    window.addEventListener('scroll', throttle(() => {
      const scrolled = window.scrollY;
      const parallaxElements = document.querySelectorAll('.transition-wave');

      parallaxElements.forEach(element => {
        const speed = 0.5;
        element.style.transform = `translateX(${scrolled * speed}px)`;
      });
    }, 10));

    // Mobile Menu Functionality
    document.addEventListener('DOMContentLoaded', function () {
      const hamburger = document.getElementById('hamburger');
      const mobileMenuOverlay = document.getElementById('mobileMenuOverlay');
      const mobileNavLinks = document.querySelectorAll('.mobile-nav-links a');

      // Toggle menu when hamburger is clicked
      hamburger.addEventListener('click', function () {
        const isOpen = mobileMenuOverlay.classList.contains('active');

        if (isOpen) {
          // Close menu
          mobileMenuOverlay.classList.remove('active');
          hamburger.classList.remove('active');
          document.body.style.overflow = '';
        } else {
          // Open menu
          mobileMenuOverlay.classList.add('active');
          hamburger.classList.add('active');
          document.body.style.overflow = 'hidden';
        }
      });

      // Close menu when clicking on a nav link
      mobileNavLinks.forEach(link => {
        link.addEventListener('click', function () {
          mobileMenuOverlay.classList.remove('active');
          hamburger.classList.remove('active');
          document.body.style.overflow = '';
        });
      });

      // Close menu when clicking outside
      mobileMenuOverlay.addEventListener('click', function (e) {
        if (e.target === mobileMenuOverlay) {
          mobileMenuOverlay.classList.remove('active');
          hamburger.classList.remove('active');
          document.body.style.overflow = '';
        }
      });
    });
  }
}
</script>

<style>
@import '@/styles.css';

/deep/a {
  text-decoration: none;
}
</style>
